import { sanitize } from 'isomorphic-dompurify';
import {
  isWwsWeddingWebsiteV2,
  type WeddingWebsiteQuery,
  isWwsError,
} from '../types/graphql';

function normalizeEmptyParagraphsAndSpaces(content: string): string {
  const withRegularSpaces = content.replace(/&nbsp;/g, ' ');

  // Preserve intentional blank lines from editors that serialize them as empty paragraphs.
  return withRegularSpaces.replace(/<p>(?:\s|<br\s*\/?>)*<\/p>/gi, '<br>');
}

export function cleanUserInput(input: string | null | undefined): string {
  // Don't run dompurify on server
  if (input !== null && typeof input !== 'undefined') {
    if (typeof window === 'undefined') {
      return normalizeEmptyParagraphsAndSpaces(input);
    }

    return normalizeEmptyParagraphsAndSpaces(sanitize(input));
  }

  return '';
}

export function cleanHTMLInput(input: string | null | undefined): string {
  if (input !== null && typeof input !== 'undefined') {
    if (typeof window === 'undefined') {
      return normalizeEmptyParagraphsAndSpaces(input);
    }

    return normalizeEmptyParagraphsAndSpaces(
      sanitize(input, {
        ALLOWED_TAGS: ['strong', 'b', 'i', 'em', 'p', 'br', 'a'],
        ALLOWED_ATTR: ['style', 'class', 'href', 'rel', 'target'],
      })
    );
  }

  return '';
}

export function cleanThemeHTML(input: string | null | undefined): string {
  if (input !== null && typeof input !== 'undefined') {
    if (typeof window === 'undefined') {
      return normalizeEmptyParagraphsAndSpaces(input);
    }

    return normalizeEmptyParagraphsAndSpaces(
      sanitize(input, {
        ALLOWED_TAGS: ['a'],
        ALLOWED_ATTR: ['style', 'class', 'href', 'target'],
      })
    );
  }

  return '';
}

/**
 * Modifies the provided HTML content by adding `rel="nofollow ugc noopener noreferrer external"` to all `<a>` tags.
 * This enhancement is aimed at improving SEO and security for links within user-generated content.
 *
 * @param {string} content - The HTML content containing `<a>` tags to be modified.
 * @returns {string} The modified HTML content with updated `<a>` tags.
 */
function addNofollowUgcToLinks(content: string): string {
  // Regular expression to match <a> tags and capture the href and rel attributes if they exist
  const linkRegex = /<a\s+href="([^"]*)"(?:\s+rel="([^"]*)")?([^>]*)>/gi;

  // Function to replace each match with a modified <a> tag
  return content.replace(linkRegex, replacer);

  function replacer(
    _match: string,
    href: string,
    _rel: string,
    rest: string
  ): string {
    // Check if the rel attribute exists and append 'nofollow ugc' to it, or add it if it doesn't exist
    const newRel = `rel="nofollow ugc noopener noreferrer external"`;

    // Reconstruct the <a> tag with the new rel attribute
    return `<a href="${href}" ${newRel} ${rest}>`;
  }
}

/**
 * Cleans the data of a wedding website by iterating over each page and its items, specifically targeting
 * ParagraphItem types. For each ParagraphItem, it applies the `addNofollowUgcToLinks` function to modify
 * the content by adding `rel="nofollow ugc noopener noreferrer external"` to all `<a>` tags found within.
 * This process is aimed at enhancing SEO and security for links within the user-generated content of the wedding website.
 *
 * The function first deep clones the input data since the original data is immutable. Then, it traverses
 * through the pages and their items. If an item is of type 'ParagraphItem', its content is processed
 * to add the necessary `rel` attributes to `<a>` tags. This ensures that the links do not pass referral
 * information and are treated appropriately by search engines, while also protecting against potential
 * security vulnerabilities associated with opening links in new tabs.
 *
 * @param {WeddingWebsiteQuery} wwData - The original wedding website data to be cleaned.
 * @returns {WeddingWebsiteQuery} The modified wedding website data with updated `<a>` tags in the content of ParagraphItem types.
 */
export function cleanWeddingWebsitePageItemContentData(
  wwData: WeddingWebsiteQuery | null
): WeddingWebsiteQuery | null {
  if (
    !wwData ||
    !wwData.weddingWebsiteResult ||
    isWwsError(wwData.weddingWebsiteResult)
  ) {
    return wwData;
  }

  // this makes a deep copy of the data and removing the immutability
  const unfrozenData = JSON.parse(JSON.stringify(wwData));

  if (
    isWwsWeddingWebsiteV2(unfrozenData.weddingWebsiteResult) &&
    unfrozenData.weddingWebsiteResult.registryNote
  ) {
    unfrozenData.weddingWebsiteResult.registryNote = addNofollowUgcToLinks(
      unfrozenData?.weddingWebsiteResult?.registryNote
    );
  }

  unfrozenData?.weddingWebsiteResult?.pages?.forEach(
    (page: {
      items: {
        description: string;
        type: string;
        content: string;
      }[];
    }): void => {
      page?.items.forEach((item): void => {
        if (item.content) {
          // TODO: Remove this condition when we do away with lite site.
          if (isWwsWeddingWebsiteV2(unfrozenData.weddingWebsiteResult)) {
            item.content = item.content.replace(/\\/g, '');
          }

          item.content = addNofollowUgcToLinks(item.content);
        } else if (item.description) {
          item.description = addNofollowUgcToLinks(item.description);
        }
      });
    }
  );

  return unfrozenData;
}
